import { IRemark } from '@Domains/utils';
import { mapAssets } from '@Utils/assetUtils';
import { RemarkFormValues } from '@Utils/Types/FormValuesTypes';
import { Role } from 'src/Enum/Report';
import { IImpactsOnStakeholdersFormValues } from '../ImpactsOnStakeholdersFormProvider/types';

export const mapToStakeholderPayload = (
  formValues: IImpactsOnStakeholdersFormValues
) => {
  const {
    remark,
    introduction,
    businessValueChain,
    stakeholderAnalysis,
    ...value
  } = formValues;
  const stakeholderGroup =
    formValues.stakeholderAnalysis?.stakeholderGroups ?? [];

  const mapBusinessValueChainAssets = mapAssets(
    formValues.businessValueChain?.assets
  );
  const mapStakeholderAssets = mapAssets(
    formValues.stakeholderAnalysis?.assets
  );

  const completedSection = formValues.completedSection
    ? {
        id: formValues.completedSection?.id ?? null,
        isCompletedTh: formValues.completedSection.isCompletedTh ?? false,
        isCompletedEn: formValues.completedSection.isCompletedEn ?? false,
        dateTh: formValues.completedSection.dateTh ?? null,
        dateEn: formValues.completedSection.dateEn ?? null,
        roleStampTh: formValues.completedSection.roleStampTh ?? Role.NONE,
        roleStampEn: formValues.completedSection.roleStampEn ?? Role.NONE,
      }
    : {
        id: null,
        isCompletedTh: false,
        isCompletedEn: false,
        dateTh: null,
        dateEn: null,
        roleStampTh: Role.NONE,
        roleStampEn: Role.NONE,
      };

  const stakeholderAnalysisCompletedSection = formValues.stakeholderAnalysis
    ?.completedSection
    ? {
        id: formValues.stakeholderAnalysis.completedSection?.id ?? null,
        isCompletedTh:
          formValues.stakeholderAnalysis.completedSection.isCompletedTh ??
          false,
        isCompletedEn:
          formValues.stakeholderAnalysis.completedSection.isCompletedEn ??
          false,
        dateTh: formValues.stakeholderAnalysis.completedSection.dateTh ?? null,
        dateEn: formValues.stakeholderAnalysis.completedSection.dateEn ?? null,
        roleStampTh:
          formValues.stakeholderAnalysis.completedSection.roleStampTh ??
          Role.NONE,
        roleStampEn:
          formValues.stakeholderAnalysis.completedSection.roleStampEn ??
          Role.NONE,
      }
    : {
        id: null,
        isCompletedTh: false,
        isCompletedEn: false,
        dateTh: null,
        dateEn: null,
        roleStampTh: Role.NONE,
        roleStampEn: Role.NONE,
      };

  const businessValueChainCompletedSection = formValues.businessValueChain
    ?.completedSection
    ? {
        id: formValues.businessValueChain.completedSection?.id ?? null,
        isCompletedTh:
          formValues.businessValueChain.completedSection.isCompletedTh ?? false,
        isCompletedEn:
          formValues.businessValueChain.completedSection.isCompletedEn ?? false,
        dateTh: formValues.businessValueChain.completedSection.dateTh ?? null,
        dateEn: formValues.businessValueChain.completedSection.dateEn ?? null,
        roleStampTh:
          formValues.businessValueChain.completedSection.roleStampTh ??
          Role.NONE,
        roleStampEn:
          formValues.businessValueChain.completedSection.roleStampEn ??
          Role.NONE,
      }
    : {
        id: null,
        isCompletedTh: false,
        isCompletedEn: false,
        dateTh: null,
        dateEn: null,
        roleStampTh: Role.NONE,
        roleStampEn: Role.NONE,
      };

  return {
    ...value,
    completedSection,
    stakeholderAnalysis: {
      ...stakeholderAnalysis,
      remark: mapRemarkToPayload(stakeholderAnalysis?.remark),
      introduction: mapIntroToPayload(stakeholderAnalysis?.introduction),
      assets: mapStakeholderAssets,
      stakeholderGroups: stakeholderGroup.map((group) => {
        const { stakeholders, communicationActivities, ...restGroup } = group;
        return {
          ...restGroup,
          stakeholders: stakeholders.map((stakeholder) => ({
            id: stakeholder.id,
            infoCategoryMasterDataId: stakeholder.infoCategoryMasterDataId,
          })),
          communicationActivities:
            communicationActivities?.map((activity) => ({
              id: activity.id,
              infoCategoryMasterDataId: activity.infoCategoryMasterDataId,
            })) ?? [],
        };
      }),
      completedSection: stakeholderAnalysisCompletedSection,
    },
    businessValueChain: {
      ...businessValueChain,
      remark: mapRemarkToPayload(businessValueChain?.remark),
      introduction: mapIntroToPayload(businessValueChain?.introduction),
      assets: mapBusinessValueChainAssets,
      completedSection: businessValueChainCompletedSection,
    },
    introduction: mapIntroToPayload(introduction),
    remark: mapRemarkToPayload(remark),
  };
};

const mapRemarkToPayload = (remark?: RemarkFormValues): IRemark | null => {
  if (!remark) return null;

  const { id, descriptionTh, descriptionEn } = remark;

  return descriptionTh || descriptionEn
    ? {
        id,
        descriptionTh: descriptionTh ?? null,
        descriptionEn: descriptionEn ?? null,
      }
    : null;
};

const mapIntroToPayload = (introduction?: RemarkFormValues): IRemark | null => {
  if (!introduction) return null;

  const { id, descriptionTh, descriptionEn } = introduction;

  return descriptionTh || descriptionEn
    ? {
        id,
        descriptionTh: descriptionTh ?? null,
        descriptionEn: descriptionEn ?? null,
      }
    : null;
};
